import { useEffect, useMemo, useState } from "react";
import { Check, MonitorCog, ShieldCheck } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import {
  captureExtensionConnectContext,
  extensionPermissionsUrl,
  isValidClientId,
  isValidExtensionId,
  openExtensionPermissions,
} from "@/lib/extensionConnect";
import { BrandLockup } from "@/shared/ui/brand";
import { Button } from "@/shared/ui/button";
import { ErrorAlert } from "@/shared/ui/error-alert";
import { useI18n } from "@/i18n/I18nContext";

export function WelcomePage() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clientId = params.get("client_id")?.trim() ?? "";
  const extensionId = params.get("extension_id")?.trim() ?? "";
  const canContinue = isValidClientId(clientId) && isValidExtensionId(extensionId);
  const permissionsUrl = useMemo(
    () =>
      canContinue
        ? extensionPermissionsUrl(extensionId, { clientId })
        : null,
    [canContinue, clientId, extensionId],
  );

  useEffect(() => {
    captureExtensionConnectContext(params.toString());
  }, [params]);

  const continueToPermissions = async () => {
    if (!canContinue || !permissionsUrl) return;
    setError(null);
    setLoading(true);
    try {
      await openExtensionPermissions(extensionId, clientId);
    } catch (nextError) {
      setError(
        nextError instanceof Error
          ? nextError.message
          : t("welcome.openError"),
      );
      window.location.assign(permissionsUrl);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-dvh bg-canvas px-6 py-10 text-foreground sm:px-10">
      <header className="mx-auto flex max-w-5xl items-center">
        <BrandLockup markSize={28} wordmarkClassName="text-xl" />
      </header>
      <section className="mx-auto grid max-w-5xl gap-12 py-16 lg:grid-cols-[1.2fr_0.8fr] lg:py-24">
        <div className="max-w-xl">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">{t("welcome.eyebrow")}</p>
          <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">{t("welcome.title")}</h1>
          <p className="mt-5 max-w-lg text-base leading-7 text-muted-foreground">
            {t("welcome.intro")}
          </p>
          <ul className="mt-8 grid gap-4 text-sm text-muted-foreground">
            {[
              t("welcome.point1"),
              t("welcome.point2"),
              t("welcome.point3"),
            ].map((item) => (
              <li className="flex gap-3" key={item}>
                <Check className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <aside className="self-center rounded-2xl border border-border bg-card p-7 shadow-sm">
          <MonitorCog className="size-7 text-primary" aria-hidden="true" />
          <h2 className="mt-5 text-xl font-semibold">{t("welcome.setupTitle")}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t("welcome.setupDescription")}
          </p>
          {canContinue ? (
            <Button className="mt-6 w-full" size="lg" loading={loading} onClick={() => void continueToPermissions()}>
              {t("welcome.continue")}
            </Button>
          ) : (
            <ErrorAlert className="mt-6" message={t("welcome.missingDetails")} />
          )}
          {error ? <ErrorAlert className="mt-4" message={error} /> : null}
          <p className="mt-5 flex gap-2 text-xs leading-5 text-muted-foreground">
            <ShieldCheck className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            {t("welcome.accessNote")}
          </p>
        </aside>
      </section>
    </main>
  );
}
